import { LoadingOutlined } from '@ant-design/icons';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import AppFileName from '@nxsys/components/AppFileName';
import {
  downloadFileByUrlWithCorrectFileName,
  formatStatus,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { find } from 'lodash';
import {
  agencyUploadTypeOptions,
  HMRC_GENDER_OPTIONS,
  HMRC_PAY_FREQUENCY_OPTIONS,
} from '../constants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { defaultCisStatus } from '@nxsys/constants/AppConstants';

export const getImportAgencyColumns = ({
  messages,
  onView,
  downloadLoadingId,
}: {
  messages: TypedMessages;
  onView: (data: IImportAgency) => void;
  downloadLoadingId?: string | null;
}): ColumnsType<IImportAgency> => {
  return [
    {
      key: 'no',
      title: 'No',
      dataIndex: 'indexNumber',
      width: 80,
      align: 'right',
    },
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-center gap-x-4">
          <li onClick={() => onView(record)}>
            <Tooltip title="View">
              <span><Icons.IconEye /></span>
            </Tooltip>
          </li>
          {record?.url && (
            <li
              className={downloadLoadingId === record.id ? 'pointer-events-none text-primary-500' : ''}
              onClick={() =>
                downloadFileByUrlWithCorrectFileName(
                  record.url,
                  record.fileName,
                )
              }
            >
              <Tooltip title={downloadLoadingId === record.id ? 'Loading...' : 'Download'}>
                <span>
                  {downloadLoadingId === record.id ? (
                    <LoadingOutlined style={{ fontSize: 16 }} spin />
                  ) : (
                    <Icons.IconDownload />
                  )}
                </span>
              </Tooltip>
            </li>
          )}
        </ul>
      ),
      width: 100,
    },
    {
      key: 'fileName',
      title: messages['common.fileName'],
      dataIndex: 'fileName',
      render: (value) => <AppFileName fileName={value} />,
    },
    {
      key: 'importOn',
      title: messages['common.importedOn'],
      dataIndex: 'importOn',
      align: 'right',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      key: 'author',
      title: messages['common.importedBy'],
      dataIndex: 'author',
    },
    {
      key: 'totalRecords',
      title: messages['common.noOfRecords'],
      dataIndex: 'totalRecords',
      align: 'right',
      render: (value) => (value ? <Tag>{value}</Tag> : '-'),
    },
  ];
};

export const getImportEmployeeColumns = ({
  messages,
  onView,
  downloadLoadingId,
}: {
  messages: TypedMessages;
  onView: (data: IImportEmployee) => void;
  downloadLoadingId?: string | null;
}): ColumnsType<IImportEmployee> => {
  return [
    {
      key: 'no',
      title: 'No',
      dataIndex: 'indexNumber',
      width: 80,
      align: 'right',
    },
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-center gap-x-4">
          <li onClick={() => onView(record)}>
            <Tooltip title="View">
              <span><Icons.IconEye /></span>
            </Tooltip>
          </li>
          {record?.url && (
            <li
              className={downloadLoadingId === record.id ? 'pointer-events-none text-primary-500' : ''}
              onClick={() =>
                downloadFileByUrlWithCorrectFileName(
                  record.url,
                  record.fileName,
                )
              }
            >
              <Tooltip title={downloadLoadingId === record.id ? 'Loading...' : 'Download'}>
                <span>
                  {downloadLoadingId === record.id ? (
                    <LoadingOutlined style={{ fontSize: 16 }} spin />
                  ) : (
                    <Icons.IconDownload />
                  )}
                </span>
              </Tooltip>
            </li>
          )}
        </ul>
      ),
      width: 100,
    },
    {
      key: 'fileName',
      title: messages['common.fileName'],
      dataIndex: 'fileName',
      render: (value) => <AppFileName fileName={value} />,
    },
    {
      key: 'importOn',
      title: messages['common.importedOn'],
      dataIndex: 'importOn',
      align: 'right',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      key: 'author',
      title: messages['common.importedBy'],
      dataIndex: 'author',
    },
    {
      key: 'totalRecords',
      title: messages['common.noOfRecords'],
      dataIndex: 'totalRecords',
      align: 'right',
      render: (value) => (value ? <Tag>{value}</Tag> : '-'),
    },
  ];
};

export const getImportProspectColumns = ({
  messages,
  onView,
  downloadLoadingId,
}: {
  messages: TypedMessages;
  onView: (data: IImportProspect) => void;
  downloadLoadingId?: string | null;
}): ColumnsType<IImportProspect> => {
  return [
    {
      key: 'no',
      title: 'No',
      dataIndex: 'indexNumber',
      width: 80,
      align: 'right',
    },
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-center gap-x-4">
          <li onClick={() => onView(record)}>
            <Tooltip title="View">
              <span><Icons.IconEye /></span>
            </Tooltip>
          </li>
          {record?.url && (
            <li
              className={downloadLoadingId === record.id ? 'pointer-events-none text-primary-500' : ''}
              onClick={() =>
                downloadFileByUrlWithCorrectFileName(
                  record.url,
                  record.fileName,
                )
              }
            >
              <Tooltip title={downloadLoadingId === record.id ? 'Loading...' : 'Download'}>
                <span>
                  {downloadLoadingId === record.id ? (
                    <LoadingOutlined style={{ fontSize: 16 }} spin />
                  ) : (
                    <Icons.IconDownload />
                  )}
                </span>
              </Tooltip>
            </li>
          )}
        </ul>
      ),
      width: 100,
    },
    {
      key: 'fileName',
      title: messages['common.fileName'],
      dataIndex: 'fileName',
      render: (value) => <AppFileName fileName={value} />,
    },
    {
      key: 'importOn',
      title: messages['common.importedOn'],
      dataIndex: 'importOn',
      align: 'right',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      key: 'author',
      title: messages['common.importedBy'],
      dataIndex: 'author',
    },
    {
      key: 'totalRecords',
      title: messages['common.noOfRecords'],
      dataIndex: 'totalRecords',
      align: 'right',
      render: (value) => (value ? <Tag>{value}</Tag> : '-'),
    },
  ];
};

export const getImportTimesheetColumns = ({
  messages,
  onView,
  downloadLoadingId,
}: {
  messages: TypedMessages;
  onView: (data: IImportTimesheet) => void;
  downloadLoadingId?: string | null;
}): ColumnsType<IImportTimesheet> => {
  return [
    {
      key: 'no',
      title: 'No',
      dataIndex: 'indexNumber',
      width: 80,
      align: 'right',
    },
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-center gap-x-4">
          <li onClick={() => onView(record)}>
            <Tooltip title="View">
              <span><Icons.IconEye /></span>
            </Tooltip>
          </li>
          {record?.url && (
            <li
              className={downloadLoadingId === record.id ? 'pointer-events-none text-primary-500' : ''}
              onClick={() =>
                downloadFileByUrlWithCorrectFileName(
                  record.url,
                  record.fileName,
                )
              }
            >
              <Tooltip title={downloadLoadingId === record.id ? 'Loading...' : 'Download'}>
                <span>
                  {downloadLoadingId === record.id ? (
                    <LoadingOutlined style={{ fontSize: 16 }} spin />
                  ) : (
                    <Icons.IconDownload />
                  )}
                </span>
              </Tooltip>
            </li>
          )}
        </ul>
      ),
      width: 100,
    },
    {
      key: 'fileName',
      title: messages['common.fileName'],
      dataIndex: 'fileName',
      render: (value) => <AppFileName fileName={value} />,
    },
    {
      key: 'importOn',
      title: messages['common.importedOn'],
      dataIndex: 'importOn',
      align: 'right',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      key: 'author',
      title: messages['common.importedBy'],
      dataIndex: 'author',
    },
    {
      key: 'totalRecords',
      title: messages['common.noOfRecords'],
      dataIndex: 'totalRecords',
      align: 'right',
      render: (value) => (value ? <Tag>{value}</Tag> : '-'),
    },
  ];
};

export const getAgencyUploadColumns = ({
  messages,
  onImport,
  onCancel,
  downloadLoadingId,
}: {
  messages: TypedMessages;
  onImport: (data: IAgencyUpload) => void;
  onCancel: (data: IAgencyUpload) => void;
  downloadLoadingId?: string | null;
}): ColumnsType<IAgencyUpload> => {
  return [
    {
      key: 'no',
      title: 'No',
      dataIndex: 'indexNumber',
      width: 80,
      align: 'right',
    },
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-center gap-x-4">
          {record?.status !== 'CANCELED' && (
            <>
              <li onClick={() => onImport(record)}>
                <Tooltip title="Import">
                  <span><Icons.IconFileImport
                    color={defaultTheme.theme.palette.brand}
                    width={16}
                    height={16}
                  /></span>
                </Tooltip>
              </li>
              <li onClick={() => onCancel(record)}>
                <Tooltip title="Cancel">
                  <span><Icons.IconClose color={defaultTheme.theme.palette.red[500]} /></span>
                </Tooltip>
              </li>
            </>
          )}
          {record?.attachmentURL && (
            <li
              className={downloadLoadingId === record.id ? 'pointer-events-none text-primary-500' : ''}
              onClick={() =>
                downloadFileByUrlWithCorrectFileName(
                  record.attachmentURL,
                  record.fileName,
                )
              }
            >
              <Tooltip title={downloadLoadingId === record.id ? 'Loading...' : 'Download'}>
                <span>
                  {downloadLoadingId === record.id ? (
                    <LoadingOutlined style={{ fontSize: 16 }} spin />
                  ) : (
                    <Icons.IconDownload />
                  )}
                </span>
              </Tooltip>
            </li>
          )}
        </ul>
      ),
      width: 130,
    },
    {
      key: 'status',
      title: messages['common.status'],
      dataIndex: 'status',
      render: (record) => {
        const status = record;
        return (
          <span className={`${formatStatus(status)}`}>
            <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
              {formatStatus(status)}
            </AppTagStatus>
          </span>
        );
      },
      width: 150,
    },
    {
      key: 'createdAt',
      title: messages['common.date'],
      dataIndex: 'createdAt',
      align: 'right',
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
      width: 150,
    },
    {
      key: 'uploadedBy',
      title: messages['common.uploadedBy'],
      dataIndex: 'author',
      render: (value) => `${value?.forename || ''} ${value?.surname || ''}`,
    },
    {
      key: 'fileType',
      title: messages['common.fileType'],
      dataIndex: 'fileType',
      render: (value) =>
        find(agencyUploadTypeOptions, (val) => val.value === value)?.label ||
        value,
      width: 300,
    },
    {
      key: 'fileName',
      title: messages['common.fileName'],
      dataIndex: 'fileName',
      render: (value) => <AppFileName fileName={value} />,
    },
    {
      key: 'note',
      title: messages['common.noteFromAgency'],
      dataIndex: 'note',
      width: 400,
    },
    {
      key: 'cancelReason',
      title: messages['common.noteFromCompany'],
      dataIndex: 'cancelReason',
      width: 400,
    },
  ];
};

export const getCISImportXMLColumns = ({
  messages,
}: {
  messages: TypedMessages;
}): ColumnsType<IImportEmployeeXmlType> => {
  return [
    {
      title: messages['common.nino'],
      dataIndex: 'nino',
      key: 'nino',
      width: 120,
    },
    {
      title: messages['common.utrNo'],
      dataIndex: 'utrNumber',
      key: 'utrNumber',
      width: 120,
    },
    {
      title: messages['common.personalVerificationNumber'],
      dataIndex: 'verificationNumber',
      key: 'verificationNumber',
      width: 200,
    },
    {
      title: messages['table.cisStatus'],
      dataIndex: 'cisStatus',
      key: 'cisStatus',
      width: 120,
      render: (value) => find(defaultCisStatus, { value })?.label,
    },
    {
      title: messages['common.tradingName'],
      dataIndex: 'tradingName',
      key: 'tradingName',
      width: 150,
    },
    {
      title: messages['common.title'],
      dataIndex: 'title',
      key: 'title',
      width: 80,
    },
    {
      title: messages['common.forename'],
      dataIndex: 'forename',
      key: 'forename',
      width: 120,
    },
    {
      title: messages['common.surname'],
      dataIndex: 'surname',
      key: 'surname',
      width: 120,
    },
  ];
};

export const getFPSImportXMLColumns = ({
  messages,
  payrollCountry,
}: {
  messages: TypedMessages;
  payrollCountry?: any;
}): ColumnsType<IImportEmployeeXmlType> => {
  return [
    {
      title: messages['common.nino'],
      dataIndex: 'nino',
      key: 'nino',
      width: 120,
    },
    {
      title: messages['common.title'],
      dataIndex: 'title',
      key: 'title',
      width: 80,
    },
    {
      title: messages['common.forename'],
      dataIndex: 'forename',
      key: 'forename',
      width: 120,
    },
    {
      title: messages['common.surname'],
      dataIndex: 'surname',
      key: 'surname',
      width: 120,
    },
    {
      title: messages['common.address'],
      dataIndex: 'address',
      key: 'address',
      width: 200,
      render: (address) =>
        Array.isArray(address) ? address.join(', ') : address,
    },
    {
      title: messages['common.postCode'],
      dataIndex: 'postCode',
      key: 'postCode',
      width: 100,
    },
    {
      title: messages['common.birthDate'],
      dataIndex: 'birthDate',
      key: 'birthDate',
      width: 100,
    },
    {
      title: messages['common.gender'],
      dataIndex: 'gender',
      key: 'gender',
      width: 80,
      render: (value: string) =>
        HMRC_GENDER_OPTIONS[value as keyof typeof HMRC_GENDER_OPTIONS] || value,
    },
    {
      title: messages['common.payId'],
      dataIndex: 'payId',
      key: 'payId',
      width: 150,
    },
    {
      title: messages['common.payFrequency'],
      dataIndex: 'payFrequency',
      key: 'payFrequency',
      width: 120,
      render: (value: string) =>
        HMRC_PAY_FREQUENCY_OPTIONS[
          value as keyof typeof HMRC_PAY_FREQUENCY_OPTIONS
        ] || value,
    },
    {
      title: messages['common.taxCode'],
      dataIndex: 'taxCode',
      key: 'taxCode',
      width: 100,
    },
    {
      title: messages['common.taxBasis'],
      dataIndex: 'taxBasis',
      key: 'taxBasis',
      width: 100,
    },
    {
      title: messages['common.oldPayrollId'],
      dataIndex: 'oldPayrollId',
      key: 'oldPayrollId',
      width: 120,
    },
    {
      title: messages['common.niCategory'],
      dataIndex: 'niCategory',
      key: 'niCategory',
      width: 100,
    },
    {
      title: messages['common.grossForTaxYTD'],
      dataIndex: 'grossForTaxYTD',
      key: 'grossForTaxYTD',
      width: 140,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.taxablePayYTD'],
      dataIndex: 'taxablePayYTD',
      key: 'taxablePayYTD',
      width: 130,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.taxYTD'],
      dataIndex: 'taxYTD',
      key: 'taxYTD',
      width: 100,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.studentLoanYTD'],
      dataIndex: 'studentLoanYTD',
      key: 'studentLoanYTD',
      width: 130,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.pgLoanYTD'],
      dataIndex: 'pgLoanYTD',
      key: 'pgLoanYTD',
      width: 120,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.eePensionYTD'],
      dataIndex: 'eePensionYTD',
      key: 'eePensionYTD',
      width: 130,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.smpYTD'],
      dataIndex: 'smpYTD',
      key: 'smpYTD',
      width: 100,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.sspYTD'],
      dataIndex: 'sspYTD',
      key: 'sspYTD',
      width: 100,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.sapYTD'],
      dataIndex: 'sapYTD',
      key: 'sapYTD',
      width: 100,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.sppYTD'],
      dataIndex: 'sppYTD',
      key: 'sppYTD',
      width: 100,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.sppaYTD'],
      dataIndex: 'sppaYTD',
      key: 'sppaYTD',
      width: 100,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.sncpYTD'],
      dataIndex: 'sncpYTD',
      key: 'sncpYTD',
      width: 100,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.spbpYTD'],
      dataIndex: 'spbpYTD',
      key: 'spbpYTD',
      width: 100,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.shppYTD'],
      dataIndex: 'shppYTD',
      key: 'shppYTD',
      width: 100,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    // National Insurance Information
    {
      title: messages['common.grossForNIYTD'],
      dataIndex: 'grossForNIYTD',
      key: 'grossForNIYTD',
      width: 130,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.atLelYTD'],
      dataIndex: 'atLelYTD',
      key: 'atLelYTD',
      width: 110,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.lelToPtYTD'],
      dataIndex: 'lelToPtYTD',
      key: 'lelToPtYTD',
      width: 120,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.ptToUelYTD'],
      dataIndex: 'ptToUelYTD',
      key: 'ptToUelYTD',
      width: 120,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.erNiYTD'],
      dataIndex: 'erNiYTD',
      key: 'erNiYTD',
      width: 110,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.eeNiYTD'],
      dataIndex: 'eeNiYTD',
      key: 'eeNiYTD',
      width: 110,
      align: 'right',
      render: (value) => (value ? formatMoney(parseFloat(value), showCurrencyByCountry(payrollCountry)) : ''),
    },
    {
      title: messages['common.workplacePostcode'],
      dataIndex: 'workplacePostcode',
      key: 'workplacePostcode',
      width: 140,
    },
    {
      title: messages['common.loanTypes'],
      dataIndex: 'loanTypes',
      key: 'loanTypes',
      width: 200,
      render: (value) => (value ? value.replace(/,/g, ', ') : ''),
    },
    {
      title: messages['common.isNIDirector'],
      dataIndex: 'isNIDirector',
      key: 'isNIDirector',
      width: 100,
      render: (value) =>
        value ? messages['common.yes'] : messages['common.no'],
    },
    {
      title: messages['common.offPayrollWorker'],
      dataIndex: 'offPayrollWorker',
      key: 'offPayrollWorker',
      width: 130,
      render: (value) =>
        value ? messages['common.yes'] : messages['common.no'],
    },
    {
      title: messages['common.irregularPaidWorker'],
      dataIndex: 'irregularPaidWorker',
      key: 'irregularPaidWorker',
      width: 140,
      render: (value) =>
        value ? messages['common.yes'] : messages['common.no'],
    },
    {
      title: messages['common.pensionCalculationType'],
      dataIndex: 'pensionCalculationType',
      key: 'pensionCalculationType',
      width: 180,
      render: (value) => (value ? value.replace(/_/g, ' ') : ''),
    },
  ];
};
